import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ID, Databases, Query } from 'appwrite';
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { useAuth } from '../appwrite-testing/AuthContext';
import { client } from '../appwrite-testing/appwriteConfig';

const NotificationContext = createContext();
const databases = new Databases(client);

// Constants for Appwrite
const DATABASE_ID = '67a945bb00367ed52fcc';
const COLLECTION_ID = '67a9461a002ee624e206';

export const useNotifications = () => {
    return useContext(NotificationContext);
};

export function NotificationProvider({ children }) {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const navigate = useNavigate();
    const isNotificationsEnabled = useFeatureIsOn("notifications");
    const { user } = useAuth();

    // Keep addNotification for local notifications
    const addNotification = (notification) => {
        if (!isNotificationsEnabled) return;
        
        setNotifications(prev => {
            const newNotification = {
                ...notification,
                id: ID.unique(),
                read: false,
                title: notification.title || 'Notification',
                message: notification.message || '',
                type: notification.type || 'info',
                link: notification.link || null,
                isExternal: notification.isExternal || false,
                onClick: notification.onClick || null,
                timestamp: new Date().toISOString(),
                isLocal: true
            };
            return [newNotification, ...prev];
        });

        // Update unread count separately
        setUnreadCount(prev => prev + 1);
    };

    // Fetch initial notifications
    useEffect(() => {
        if (!isNotificationsEnabled || !user) return;

        const fetchNotifications = async () => {
            try {
                const response = await databases.listDocuments(
                    DATABASE_ID,
                    COLLECTION_ID,
                    [
                        Query.equal('userId', user.$id),
                        Query.orderDesc('$createdAt'),
                    ]
                );
                
                const fetchedNotifications = response.documents.map(doc => ({
                    id: doc.$id,
                    title: doc.title,
                    message: doc.message,
                    read: doc.read,
                    type: doc.type,
                    link: doc.link,
                    isExternal: doc.isExternal,
                    timestamp: doc.$createdAt,
                    isLocal: false // explicitly mark as not local
                }));

                // Merge notifications preserving local ones
                setNotifications(prev => {
                    const localNotifications = prev.filter(n => n.isLocal);
                    return [...localNotifications, ...fetchedNotifications];
                });

                // Recalculate total unread count
                setNotifications(currentNotifications => {
                    const totalUnread = currentNotifications.filter(n => !n.read).length;
                    setUnreadCount(totalUnread);
                    return currentNotifications;
                });
                
                console.log("fetchedNotifications: ", fetchedNotifications);
            } catch (error) {
                console.error('Error fetching notifications:', error);
            }
        };

        fetchNotifications();
    }, [user, isNotificationsEnabled]);

    // Subscribe to realtime updates
    useEffect(() => {
        if (!isNotificationsEnabled || !user) return;

        // Subscribe to specific database collection for notifications
        const unsubscribe = client.subscribe(
            [`databases.${DATABASE_ID}.collections.${COLLECTION_ID}.documents`], 
            response => {
                const { events, payload } = response;

                // Handle document creation
                if (events.includes(`databases.${DATABASE_ID}.collections.${COLLECTION_ID}.documents.*.create`)) {
                    if (payload.userId === user.$id) {
                        const newNotification = {
                            id: payload.$id,
                            title: payload.title,
                            message: payload.message,
                            read: payload.read,
                            type: payload.type,
                            link: payload.link,
                            isExternal: payload.isExternal,
                            timestamp: payload.$createdAt,
                            isLocal: false
                        };
                        
                        setNotifications(prev => [newNotification, ...prev]);
                        if (!payload.read) {
                            setUnreadCount(count => count + 1);
                        }
                    }
                }

                // Handle document update (for read status)
                if (events.includes(`databases.${DATABASE_ID}.collections.${COLLECTION_ID}.documents.*.update`)) {
                    setNotifications(prev => {
                        const wasRead = prev.find(n => n.id === payload.$id)?.read;
                        const isNowRead = payload.read;
                        
                        // Update notifications
                        const updatedNotifications = prev.map(notif => 
                            notif.id === payload.$id 
                                ? {
                                    ...notif,
                                    read: payload.read,
                                    title: payload.title,
                                    message: payload.message,
                                    type: payload.type,
                                    link: payload.link,
                                    isExternal: payload.isExternal
                                  }
                                : notif
                        );

                        // Update unread count based on read status change
                        if (wasRead && !isNowRead) {
                            // Notification was marked as unread
                            setUnreadCount(count => count + 1);
                        } else if (!wasRead && isNowRead) {
                            // Notification was marked as read
                            setUnreadCount(count => Math.max(0, count - 1));
                        }

                        return updatedNotifications;
                    });
                }

                // Handle document deletion
                if (events.includes(`databases.${DATABASE_ID}.collections.${COLLECTION_ID}.documents.*.delete`)) {
                    setNotifications(prev => {
                        const notification = prev.find(n => n.id === payload.$id);
                        if (notification && !notification.read) {
                            setUnreadCount(count => Math.max(0, count - 1));
                        }
                        return prev.filter(n => n.id !== payload.$id);
                    });
                }
            }
        );

        // Cleanup subscription on unmount
        return () => {
            unsubscribe();
        };
    }, [user, isNotificationsEnabled]);

    const deleteNotification = async (notificationId) => {
        if (!isNotificationsEnabled) return;
        
        try {
            const notification = notifications.find(n => n.id === notificationId);
            
            // Optimistically update UI
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            if (notification && !notification.read) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }

            if (!notification?.isLocal) {
                // Handle Appwrite notification deletion
                await databases.deleteDocument(
                    DATABASE_ID,
                    COLLECTION_ID,
                    notificationId
                );
                // No need to update state here as we've already done it optimistically
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
            // Revert the optimistic update if the deletion failed
            const notification = notifications.find(n => n.id === notificationId);
            if (notification) {
                setNotifications(prev => [...prev, notification]);
                if (!notification.read) {
                    setUnreadCount(prev => prev + 1);
                }
            }
        }
    };

    const markAsRead = async (notificationId) => {
        if (!isNotificationsEnabled) return;
        
        try {
            const notification = notifications.find(n => n.id === notificationId);
            if (!notification || notification.read) return; // Skip if already read
            
            // Optimistically update UI
            setNotifications(prev => 
                prev.map(notif => 
                    notif.id === notificationId ? { ...notif, read: true } : notif
                )
            );
            setUnreadCount(prev => Math.max(0, prev - 1));

            if (!notification.isLocal) {
                // Handle Appwrite notification update
                await databases.updateDocument(
                    DATABASE_ID,
                    COLLECTION_ID,
                    notificationId,
                    {
                        read: true
                    }
                );
                // No need to update state here as we've already done it optimistically
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
            // Revert the optimistic update if the update failed
            setNotifications(prev => 
                prev.map(notif => 
                    notif.id === notificationId ? { ...notif, read: false } : notif
                )
            );
            setUnreadCount(prev => prev + 1);
        }
    };

    const handleNotificationClick = (notification) => {
        if (!isNotificationsEnabled) return;

        if (notification.onClick) {
            notification.onClick();
            return;
        }

        if (notification.link) {
            if (notification.isExternal) {
                window.open(notification.link, '_blank');
            } else {
                navigate(notification.link);
            }
        }
    };

    const clearAll = async () => {
        if (!isNotificationsEnabled) return;
        try {
            // Separate local and Appwrite notifications
            const localNotifications = notifications.filter(n => n.isLocal);
            const appwriteNotifications = notifications.filter(n => !n.isLocal);

            // Delete Appwrite notifications
            const promises = appwriteNotifications.map(notification => 
                databases.deleteDocument(
                    DATABASE_ID,
                    COLLECTION_ID,
                    notification.id
                )
            );
            await Promise.all(promises);
            
            // Clear all notifications from state
            setNotifications([]);
            setUnreadCount(0);
        } catch (error) {
            console.error('Error clearing notifications:', error);
        }
    };

    const value = {
        notifications: isNotificationsEnabled ? notifications : [],
        unreadCount: isNotificationsEnabled ? unreadCount : 0,
        addNotification,
        markAsRead,
        clearAll,
        deleteNotification,
        handleNotificationClick
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
} 